/**
 * Trim-field utilities for aligner SDF pipelines (V1-informed).
 * Trims on the filled SDF before iso-surface extraction; no XY skirt shave.
 */

import {
  BufferGeometry,
  DoubleSide,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Raycaster,
  Vector3,
} from 'three';
import {
  buildPolygon2D,
  distanceToPolygon,
  interpolateTrimCurveZ,
  pointInPolygon,
  type Polygon2D,
  type VoxelGrid,
} from '@modules/three/common/voxels';

export type TrimProfileCurve = {
  mode: 'curve';
  defaultZ: number;
  points: Vector3[];
  polygon: Polygon2D;
  segments: Array<{ ax: number; ay: number; az: number; bx: number; by: number; bz: number }>;
};

export type TrimHeightMap = {
  heights: Float32Array;
  width: number;
  height: number;
  validCells: number;
};

export type TrimFieldStats = {
  belowCutModified: number;
  insertionOpenModified: number;
  gumBelowModified: number;
  heightMapCells: number;
};

export function buildTrimProfile(points: Vector3[]): TrimProfileCurve {
  const adjusted = points.map((p) => p.clone());
  const polygon = buildPolygon2D(adjusted);
  const segments: TrimProfileCurve['segments'] = [];

  for (let i = 0; i < adjusted.length; i++) {
    const a = adjusted[i];
    const b = adjusted[(i + 1) % adjusted.length];
    segments.push({ ax: a.x, ay: a.y, az: a.z, bx: b.x, by: b.y, bz: b.z });
  }

  const defaultZ = adjusted.reduce((sum, p) => sum + p.z, 0) / adjusted.length;
  return { mode: 'curve', defaultZ, points: adjusted, polygon, segments };
}

export function buildTrimHeightMap(grid: VoxelGrid, profile: TrimProfileCurve): TrimHeightMap {
  const { counts, cellSize, origin } = grid.info;
  const width = counts.x;
  const height = counts.y;
  const heights = new Float32Array(width * height);
  let validCells = 0;

  for (let iy = 0; iy < height; iy++) {
    const wy = origin.y + iy * cellSize;
    for (let ix = 0; ix < width; ix++) {
      const wx = origin.x + ix * cellSize;
      const trimZ = interpolateTrimCurveZ(wx, wy, profile.points) ?? profile.defaultZ;
      heights[iy * width + ix] = trimZ;
      validCells++;
    }
  }

  return { heights, width, height, validCells };
}

export function buildGumHeightMap(
  gumMesh: Mesh,
  alignMatrix: Matrix4,
  grid: VoxelGrid,
): TrimHeightMap {
  gumMesh.updateMatrixWorld(true);

  const gumGeom = (gumMesh.geometry as BufferGeometry).clone();
  gumGeom.applyMatrix4(gumMesh.matrixWorld);
  gumGeom.applyMatrix4(alignMatrix);
  gumGeom.computeBoundingBox();

  const raycaster = new Raycaster();
  const rayMesh = new Mesh(gumGeom, new MeshBasicMaterial({ side: DoubleSide }));
  rayMesh.updateMatrixWorld(true);

  const { counts, cellSize, origin, bounds } = grid.info;
  const width = counts.x;
  const height = counts.y;
  const heights = new Float32Array(width * height);
  heights.fill(NaN);

  const rayOrigin = new Vector3();
  const direction = new Vector3(0, 0, -1);
  const startZ = bounds.max.z + 5;

  for (let iy = 0; iy < height; iy++) {
    const wy = origin.y + iy * cellSize;
    for (let ix = 0; ix < width; ix++) {
      const wx = origin.x + ix * cellSize;
      rayOrigin.set(wx, wy, startZ);
      raycaster.set(rayOrigin, direction);
      const hits = raycaster.intersectObject(rayMesh, false);
      if (hits.length > 0) {
        heights[iy * width + ix] = hits[0].point.z;
      }
    }
  }

  fillHeightMapVoronoi(heights, width, height);

  (rayMesh.material as MeshBasicMaterial).dispose();
  gumGeom.dispose();

  let validCells = 0;
  for (let i = 0; i < heights.length; i++) {
    if (Number.isFinite(heights[i])) validCells++;
  }

  return { heights, width, height, validCells };
}

/** Per column use max(trimZ, gumZ) so gum raycast never lowers the user trim curve. */
export function mergeTrimAndGumHeightMaps(trim: TrimHeightMap, gum: TrimHeightMap): TrimHeightMap {
  const heights = new Float32Array(trim.heights.length);
  let validCells = 0;

  for (let i = 0; i < heights.length; i++) {
    const t = trim.heights[i];
    const g = gum.heights[i];
    if (Number.isFinite(g)) {
      heights[i] = Number.isFinite(t) ? Math.max(t, g) : g;
    } else {
      heights[i] = t;
    }
    if (Number.isFinite(heights[i])) validCells++;
  }

  return { ...trim, heights, validCells };
}

/**
 * V1-style trim on filled SDF before iso: force outside below trim height only.
 * Do NOT carve insertion-side on the grid (creates vertical cliffs → MC walls → thicken curtains).
 */
export function applyTrimFieldToSdf(
  sdf: VoxelGrid,
  profile: TrimProfileCurve,
  heightMap: TrimHeightMap,
  trimMargin: number,
): { grid: VoxelGrid; stats: TrimFieldStats } {
  const result = sdf.clone();
  const belowCutModified = applyHeightMapBelowCutToSdf(result, profile, heightMap, trimMargin);

  return {
    grid: result,
    stats: {
      belowCutModified,
      insertionOpenModified: 0,
      gumBelowModified: 0,
      heightMapCells: heightMap.validCells,
    },
  };
}

export function isColumnNearTrim(
  wx: number,
  wy: number,
  profile: TrimProfileCurve,
  xyMargin: number,
): boolean {
  return columnInTrimScope(wx, wy, profile, xyMargin);
}

type NeighborList = ReadonlyArray<readonly [number, number, number]>;
const NEIGHBORS_6: NeighborList = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

/** Label 6-connected band components — exported for debug coloring. */
export function labelShellBandComponents(grid: VoxelGrid): {
  labels: Int32Array;
  sizes: number[];
} {
  return labelBandComponents(grid);
}

function labelBandComponents(grid: VoxelGrid): { labels: Int32Array; sizes: number[] } {
  const { total, counts } = grid.info;
  const labels = new Int32Array(total).fill(-1);
  const stack = new Int32Array(total);
  const sizes: number[] = [];

  for (let start = 0; start < total; start++) {
    if (grid.data[start] >= 0 || labels[start] !== -1) continue;

    let sp = 0;
    let count = 0;
    stack[sp++] = start;
    labels[start] = sizes.length;

    while (sp > 0) {
      const idx = stack[--sp];
      count++;
      const { x, y, z } = grid.coords(idx);
      for (const [dx, dy, dz] of NEIGHBORS_6) {
        const nx = x + dx;
        const ny = y + dy;
        const nz = z + dz;
        if (nx < 0 || nx >= counts.x || ny < 0 || ny >= counts.y || nz < 0 || nz >= counts.z) {
          continue;
        }
        const nIdx = grid.index(nx, ny, nz);
        if (grid.data[nIdx] >= 0 || labels[nIdx] !== -1) continue;
        labels[nIdx] = labels[start];
        stack[sp++] = nIdx;
      }
    }

    sizes.push(count);
  }

  return { labels, sizes };
}

function applyHeightMapBelowCutToSdf(
  sdf: VoxelGrid,
  profile: TrimProfileCurve,
  heightMap: TrimHeightMap,
  trimMargin: number,
): number {
  const { counts, cellSize, origin } = sdf.info;
  const xyMargin = Math.max(trimMargin, cellSize * 2);
  let modified = 0;

  for (let ix = 0; ix < counts.x; ix++) {
    const wx = origin.x + ix * cellSize;
    for (let iy = 0; iy < counts.y; iy++) {
      const wy = origin.y + iy * cellSize;
      if (!columnInTrimScope(wx, wy, profile, xyMargin)) continue;

      const trimZ = heightMap.heights[iy * heightMap.width + ix];
      if (!Number.isFinite(trimZ)) continue;

      for (let iz = 0; iz < counts.z; iz++) {
        const wz = origin.z + iz * cellSize;
        if (wz >= trimZ - trimMargin) continue;

        const idx = sdf.index(ix, iy, iz);
        const depth = trimZ - trimMargin - wz;
        const maskValue = depth + 0.1;
        if (sdf.data[idx] < maskValue) {
          sdf.data[idx] = maskValue;
          modified++;
        }
      }
    }
  }

  return modified;
}

export function nearestTrimZ(profile: TrimProfileCurve, x: number, y: number): number {
  let bestDistSq = Infinity;
  let nearestZ = profile.defaultZ;

  for (const seg of profile.segments) {
    const dx = seg.bx - seg.ax;
    const dy = seg.by - seg.ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq > 1e-6 ? ((x - seg.ax) * dx + (y - seg.ay) * dy) / lenSq : 0;
    t = Math.max(0, Math.min(1, t));
    const px = seg.ax + t * dx;
    const py = seg.ay + t * dy;
    const pz = seg.az + t * (seg.bz - seg.az);
    const distSq = (x - px) ** 2 + (y - py) ** 2;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      nearestZ = pz;
    }
  }

  return nearestZ;
}

function columnInTrimScope(wx: number, wy: number, profile: TrimProfileCurve, xyMargin: number): boolean {
  const inside = pointInPolygon(wx, wy, profile.polygon);
  if (inside) return true;
  return distanceToPolygon(wx, wy, profile.polygon) <= xyMargin;
}

function fillHeightMapVoronoi(heights: Float32Array, width: number, height: number): void {
  const count = width * height;
  const nearest = new Int32Array(count).fill(-1);

  for (let i = 0; i < count; i++) {
    if (Number.isFinite(heights[i])) nearest[i] = i;
  }

  const distSq = (idx1: number, idx2: number): number => {
    const x1 = idx1 % width;
    const y1 = Math.floor(idx1 / width);
    const x2 = idx2 % width;
    const y2 = Math.floor(idx2 / width);
    return (x1 - x2) ** 2 + (y1 - y2) ** 2;
  };

  const checkNeighbor = (idx: number, nIdx: number): void => {
    if (nIdx < 0 || nIdx >= count) return;
    const sourceIdx = nearest[nIdx];
    if (sourceIdx === -1) return;
    const d = distSq(idx, sourceIdx);
    const currentSource = nearest[idx];
    if (currentSource === -1 || d < distSq(idx, currentSource)) {
      nearest[idx] = sourceIdx;
    }
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (x > 0) checkNeighbor(idx, idx - 1);
      if (y > 0) checkNeighbor(idx, idx - width);
      if (x > 0 && y > 0) checkNeighbor(idx, idx - width - 1);
      if (x < width - 1 && y > 0) checkNeighbor(idx, idx - width + 1);
    }
  }

  for (let y = height - 1; y >= 0; y--) {
    for (let x = width - 1; x >= 0; x--) {
      const idx = y * width + x;
      if (x < width - 1) checkNeighbor(idx, idx + 1);
      if (y < height - 1) checkNeighbor(idx, idx + width);
      if (x < width - 1 && y < height - 1) checkNeighbor(idx, idx + width + 1);
      if (x > 0 && y < height - 1) checkNeighbor(idx, idx + width - 1);
    }
  }

  for (let i = 0; i < count; i++) {
    if (!Number.isFinite(heights[i]) && nearest[i] !== -1) {
      heights[i] = heights[nearest[i]];
    }
  }
}
